import { useEffect, type RefObject } from 'react';

type UsePlatformChatScrollEffectsOptions = {
  chatId: string | undefined;
  displayMessageCount: number;
  isMessagesLoading: boolean;
  checkIsAtBottom: () => void;
  scrollToBottom: (options?: { behavior?: ScrollBehavior }) => void;
  hasInitialScrollRef: RefObject<boolean>;
  isLoadingOlderMessagesRef: RefObject<boolean>;
};

/** Resets and syncs transcript scroll when the session or message count changes. */
export function usePlatformChatScrollEffects({
  chatId,
  displayMessageCount,
  isMessagesLoading,
  checkIsAtBottom,
  scrollToBottom,
  hasInitialScrollRef,
  isLoadingOlderMessagesRef,
}: UsePlatformChatScrollEffectsOptions): void {
  useEffect(() => {
    hasInitialScrollRef.current = false;
    isLoadingOlderMessagesRef.current = false;
  }, [chatId, hasInitialScrollRef, isLoadingOlderMessagesRef]);

  useEffect(() => {
    const frame = requestAnimationFrame(checkIsAtBottom);
    return () => cancelAnimationFrame(frame);
  }, [displayMessageCount, checkIsAtBottom]);

  useEffect(() => {
    if (
      isMessagesLoading ||
      displayMessageCount === 0 ||
      hasInitialScrollRef.current
    ) {
      return;
    }

    scrollToBottom({ behavior: 'auto' });
    hasInitialScrollRef.current = true;
  }, [
    displayMessageCount,
    hasInitialScrollRef,
    isMessagesLoading,
    scrollToBottom,
  ]);
}
